import moment from "moment";
import { getTrackers, TRACKER_STATUS } from "./trackerStorage";
import {
  getSubmissions,
  getSubmissionPersonKey,
  SUBMISSION_REVIEW,
  SUBMISSION_SOURCES,
} from "./submissionStorage";
import {
  buildSubmissionPerformance,
  formatDelta,
  formatMetricValue,
} from "./submissionPerformance";

const isLiveTracker = (tracker) =>
  tracker?.status === TRACKER_STATUS.LIVE || tracker?.status === "published";

const finishStatusNames = (tracker) => {
  const names = new Set();
  (tracker?.statuses || []).forEach((s) => {
    if (s?.isFinish && s?.name) names.add(String(s.name).toLowerCase());
  });
  if (!names.size) {
    names.add("journey completed");
    names.add("completed");
    names.add("finished");
  }
  return names;
};

const dropStatusNames = (tracker) => {
  const names = new Set();
  (tracker?.statuses || []).forEach((s) => {
    const n = String(s?.name || "").toLowerCase();
    if (n.includes("drop") || n.includes("quit") || n.includes("cancel")) {
      names.add(n);
    }
  });
  if (!names.size) names.add("dropped out");
  return names;
};

const isFinishStatus = (statusName, tracker) => {
  if (!statusName) return false;
  return finishStatusNames(tracker).has(String(statusName).toLowerCase());
};

const isDropStatus = (statusName, tracker) => {
  if (!statusName) return false;
  return dropStatusNames(tracker).has(String(statusName).toLowerCase());
};

const personDisplayName = (row) =>
  row?.memberName ||
  `${row?.firstName || ""} ${row?.lastName || ""}`.trim() ||
  row?.email ||
  "Unknown";

const latestByPerson = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    const key = getSubmissionPersonKey(row);
    if (!key) return;
    const prev = map.get(key);
    if (
      !prev ||
      moment(row.submittedAt).valueOf() > moment(prev.submittedAt).valueOf()
    ) {
      map.set(key, row);
    }
  });
  return map;
};

const checkpointTarget = (tracker) => {
  const forms = (tracker?.forms || []).length;
  if (forms > 0) return forms;
  const points = (tracker?.submissionSchedule?.relativeOffsets || []).length;
  if (points > 0) return points + 1;
  return 3;
};

const activityLabel = (daysSince) => {
  if (daysSince == null || Number.isNaN(daysSince)) return "unknown";
  if (daysSince <= 7) return "active";
  if (daysSince <= 21) return "cooling";
  return "at_risk";
};

/** Overall verdict from measurable metrics (prefers 2+ reading series). */
export const summarizeClientPerformance = (latestSubmission) => {
  if (!latestSubmission) {
    return {
      verdict: "none",
      label: "No data",
      detail: "No check-ins yet",
      up: 0,
      down: 0,
      flat: 0,
      primary: null,
      metrics: [],
    };
  }

  const { metrics } = buildSubmissionPerformance(latestSubmission);
  const trendable = metrics.filter((m) => m.points?.length >= 2);
  const pool = trendable.length ? trendable : metrics;
  const up = pool.filter((m) => m.improved === "up").length;
  const down = pool.filter((m) => m.improved === "down").length;
  const flat = pool.length - up - down;

  let verdict = "none";
  let label = "No measures";
  let detail = "No measurable answers yet";
  if (pool.length) {
    if (up > 0 && down === 0) {
      verdict = "up";
      label = "Improving";
      detail = `${up} metric${up === 1 ? "" : "s"} vs baseline`;
    } else if (down > 0 && up === 0) {
      verdict = "down";
      label = "Declining";
      detail = `${down} metric${down === 1 ? "" : "s"} off track`;
    } else if (up > 0 && down > 0) {
      verdict = "mixed";
      label = "Mixed";
      detail = `${up} up · ${down} down`;
    } else {
      verdict = "flat";
      label = "Stable";
      detail = "Holding vs baseline";
    }
  }

  const primary =
    [...pool].sort(
      (a, b) =>
        (b.points?.length || 0) - (a.points?.length || 0) ||
        Math.abs(b.delta || 0) - Math.abs(a.delta || 0)
    )[0] || null;

  return {
    verdict,
    label,
    detail,
    up,
    down,
    flat,
    primary: primary
      ? {
          id: primary.id,
          label: primary.label,
          baseline: primary.baseline,
          current: primary.current,
          delta: primary.delta,
          unit: primary.unit,
          improved: primary.improved,
          pctChange: primary.pctChange,
          readings: primary.points?.length || 0,
          baselineText: formatMetricValue(primary.baseline, primary.unit),
          currentText: formatMetricValue(primary.current, primary.unit),
          deltaText: formatDelta(
            primary.delta,
            primary.unit,
            primary.improved
          ),
        }
      : null,
    metrics: pool.map((m) => ({
      id: m.id,
      label: m.label,
      improved: m.improved,
      deltaText: formatDelta(m.delta, m.unit, m.improved),
      currentText: formatMetricValue(m.current, m.unit),
    })),
  };
};


/**
 * Build accountability / performance stats for the admin dashboard
 * from local tracker + submission storage (no API required).
 */
export const buildTrackerDashboardStats = ({
  trackers = getTrackers(),
  submissions = getSubmissions(),
  trendDays = 14,
} = {}) => {
  const allSubs = submissions || [];
  const allTrackers = trackers || [];
  const trackerById = new Map(allTrackers.map((t) => [t.id, t]));
  const liveTrackers = allTrackers.filter(isLiveTracker);
  const peopleLatest = latestByPerson(allSubs);
  const uniquePeople = peopleLatest.size;
  const now = moment();

  let completedPeople = 0;
  let droppedPeople = 0;
  let inProgressPeople = 0;
  peopleLatest.forEach((row) => {
    const tracker = trackerById.get(row.trackerId);
    if (isFinishStatus(row.statusName, tracker)) completedPeople += 1;
    else if (isDropStatus(row.statusName, tracker)) droppedPeople += 1;
    else inProgressPeople += 1;
  });

  const reviewsPending = allSubs.filter(
    (r) => r.reviewStatus === SUBMISSION_REVIEW.PENDING
  ).length;
  const reviewsOverdue = allSubs.filter(
    (r) => r.reviewStatus === SUBMISSION_REVIEW.OVERDUE
  ).length;
  const reviewsDue = reviewsPending + reviewsOverdue;

  const reviewBreakdown = {
    pending: 0,
    overdue: 0,
    reviewed: 0,
    none: 0,
  };
  allSubs.forEach((r) => {
    if (r.reviewStatus === SUBMISSION_REVIEW.PENDING) reviewBreakdown.pending += 1;
    else if (r.reviewStatus === SUBMISSION_REVIEW.OVERDUE)
      reviewBreakdown.overdue += 1;
    else if (r.reviewStatus === SUBMISSION_REVIEW.REVIEWED)
      reviewBreakdown.reviewed += 1;
    else reviewBreakdown.none += 1;
  });

  const sourceBreakdown = { member: 0, public: 0 };
  allSubs.forEach((r) => {
    if (r.source === SUBMISSION_SOURCES.PUBLIC) sourceBreakdown.public += 1;
    else sourceBreakdown.member += 1;
  });

  // —— This week vs last week ——
  const startThisWeek = moment().startOf("week");
  const startLastWeek = moment().subtract(1, "week").startOf("week");
  const endLastWeek = moment().subtract(1, "week").endOf("week");

  const inRange = (iso, start, end) => {
    const m = moment(iso);
    return m.isSameOrAfter(start) && m.isSameOrBefore(end);
  };

  const thisWeekSubs = allSubs.filter((r) =>
    inRange(r.submittedAt, startThisWeek, now)
  ).length;
  const lastWeekSubs = allSubs.filter((r) =>
    inRange(r.submittedAt, startLastWeek, endLastWeek)
  ).length;

  const peopleActiveThisWeek = new Set();
  const peopleActiveLastWeek = new Set();
  allSubs.forEach((r) => {
    const key = getSubmissionPersonKey(r);
    if (!key) return;
    if (inRange(r.submittedAt, startThisWeek, now)) peopleActiveThisWeek.add(key);
    if (inRange(r.submittedAt, startLastWeek, endLastWeek))
      peopleActiveLastWeek.add(key);
  });

  // —— Submissions trend ——
  const dayLabels = [];
  const dayCounts = [];
  const dayNewPeople = [];
  const firstSeen = new Map();
  [...allSubs]
    .sort((a, b) => moment(a.submittedAt).valueOf() - moment(b.submittedAt).valueOf())
    .forEach((r) => {
      const key = getSubmissionPersonKey(r);
      if (key && !firstSeen.has(key)) firstSeen.set(key, r.submittedAt);
    });

  for (let i = trendDays - 1; i >= 0; i -= 1) {
    const day = moment().subtract(i, "days").startOf("day");
    dayLabels.push(day.format("DD MMM"));
    dayCounts.push(
      allSubs.filter((r) => moment(r.submittedAt).isSame(day, "day")).length
    );
    let newOnDay = 0;
    firstSeen.forEach((at) => {
      if (moment(at).isSame(day, "day")) newOnDay += 1;
    });
    dayNewPeople.push(newOnDay);
  }

  // —— Per-tracker performance ——
  const trackerPerf = allTrackers
    .map((tracker) => {
      const rows = allSubs.filter((r) => r.trackerId === tracker.id);
      const peopleMap = latestByPerson(rows);
      let completed = 0;
      let dropped = 0;
      let atRisk = 0;
      let active = 0;
      peopleMap.forEach((row) => {
        if (isFinishStatus(row.statusName, tracker)) completed += 1;
        else if (isDropStatus(row.statusName, tracker)) dropped += 1;
        const days = moment().diff(moment(row.submittedAt), "days");
        const label = activityLabel(days);
        if (label === "at_risk" && !isFinishStatus(row.statusName, tracker))
          atRisk += 1;
        if (label === "active") active += 1;
      });
      const people = peopleMap.size;
      const due = rows.filter(
        (r) =>
          r.reviewStatus === SUBMISSION_REVIEW.PENDING ||
          r.reviewStatus === SUBMISSION_REVIEW.OVERDUE
      ).length;
      const overdue = rows.filter(
        (r) => r.reviewStatus === SUBMISSION_REVIEW.OVERDUE
      ).length;
      const completionRate = people
        ? Math.round((completed / people) * 100)
        : 0;
      const target = checkpointTarget(tracker);
      let progressSum = 0;
      peopleMap.forEach((latest) => {
        const personRows = rows.filter(
          (r) => getSubmissionPersonKey(r) === getSubmissionPersonKey(latest)
        );
        const formsDone = new Set(personRows.map((r) => r.formId).filter(Boolean))
          .size;
        progressSum += Math.min(100, Math.round((formsDone / target) * 100));
      });
      const avgProgress = people ? Math.round(progressSum / people) : 0;
      return {
        id: tracker.id,
        name: tracker.name || "Untitled",
        status: tracker.status,
        live: isLiveTracker(tracker),
        people,
        submissions: rows.length,
        completed,
        dropped,
        atRisk,
        active,
        completionRate,
        avgProgress,
        reviewsDue: due,
        overdue,
        coaches: (tracker.coaches || []).length,
        checkpoints: target,
        score:
          people * 3 +
          rows.length +
          completionRate +
          avgProgress -
          overdue * 5 -
          atRisk * 3,
      };
    })
    .sort((a, b) => b.score - a.score);

  // —— Coach accountability ——
  const coachMap = new Map();
  const bumpCoach = (id, name, patch) => {
    const key = id || name || "unassigned";
    if (!coachMap.has(key)) {
      coachMap.set(key, {
        id: key,
        name: name || (key === "unassigned" ? "Unassigned" : "Coach"),
        people: new Set(),
        submissions: 0,
        pending: 0,
        overdue: 0,
        reviewed: 0,
        trackers: new Set(),
        lastActivity: null,
        lastReviewAt: null,
        atRiskPeople: new Set(),
        quietPeople: new Set(),
        offTrackPeople: new Set(),
        overdueSamples: [],
        stalePending: 0,
        lateReviewDays: [],
      });
    }
    const c = coachMap.get(key);
    if (patch.personKey) c.people.add(patch.personKey);
    if (patch.trackerId) c.trackers.add(patch.trackerId);
    if (patch.submission) c.submissions += 1;
    if (patch.pending) c.pending += 1;
    if (patch.overdue) c.overdue += 1;
    if (patch.reviewed) c.reviewed += 1;
    if (patch.atRisk && patch.personKey) c.atRiskPeople.add(patch.personKey);
    if (patch.quiet && patch.personKey) c.quietPeople.add(patch.personKey);
    if (patch.offTrack && patch.personKey) c.offTrackPeople.add(patch.personKey);
    if (patch.stalePending) c.stalePending += 1;
    if (patch.overdueSample) c.overdueSamples.push(patch.overdueSample);
    if (typeof patch.lateDays === "number") c.lateReviewDays.push(patch.lateDays);
    if (patch.at) {
      if (!c.lastActivity || moment(patch.at).isAfter(moment(c.lastActivity))) {
        c.lastActivity = patch.at;
      }
    }
    if (patch.reviewedAt) {
      if (
        !c.lastReviewAt ||
        moment(patch.reviewedAt).isAfter(moment(c.lastReviewAt))
      ) {
        c.lastReviewAt = patch.reviewedAt;
      }
    }
  };

  allSubs.forEach((row) => {
    const coachId = row.coachId || "";
    const coachName = row.coachName || "";
    const personKey = getSubmissionPersonKey(row);
    const days = moment().diff(moment(row.submittedAt), "days");
    const hours = moment().diff(moment(row.submittedAt), "hours");
    const tracker = trackerById.get(row.trackerId);
    const finished = isFinishStatus(row.statusName, tracker);
    const dropped = isDropStatus(row.statusName, tracker);
    const isReviewed = row.reviewStatus === SUBMISSION_REVIEW.REVIEWED;
    const isPending = row.reviewStatus === SUBMISSION_REVIEW.PENDING;
    const isOverdue = row.reviewStatus === SUBMISSION_REVIEW.OVERDUE;
    const withinTwoWeeks = days <= 14;
    const stalePending = isPending && withinTwoWeeks && hours >= 48;
    const perf = summarizeClientPerformance(row);
    const offTrack =
      !finished &&
      !dropped &&
      (perf?.verdict === "down" || (typeof perf?.primary?.delta === "number" && perf.primary.delta < 0));

    let lateDays = null;
    if (isReviewed && row.updatedAt && row.submittedAt) {
      const wait = moment(row.updatedAt).diff(moment(row.submittedAt), "days", true);
      if (wait >= 2) lateDays = Math.round(wait * 10) / 10;
    }

    bumpCoach(coachId, coachName, {
      personKey,
      trackerId: row.trackerId,
      submission: true,
      pending: isPending,
      overdue: isOverdue,
      reviewed: isReviewed,
      at: row.submittedAt,
      reviewedAt: isReviewed ? row.updatedAt || row.submittedAt : null,
      atRisk:
        activityLabel(days) === "at_risk" && !finished && !dropped,
      quiet: days >= 14 && !finished && !dropped,
      offTrack,
      stalePending,
      lateDays,
      overdueSample: isOverdue
        ? {
            person: personDisplayName(row),
            form: row.formName || "Review",
            days: Math.max(1, days),
          }
        : null,
    });
  });

  allTrackers.forEach((tracker) => {
    (tracker.coaches || []).forEach((coach) => {
      const name =
        `${coach.first_name || ""} ${coach.last_name || ""}`.trim() ||
        coach.email ||
        "Coach";
      bumpCoach(coach.id, name, { trackerId: tracker.id });
    });
  });

  const coaches = Array.from(coachMap.values())
    .map((c) => {
      const people = c.people.size;
      const reviewTotal = c.pending + c.overdue + c.reviewed;
      const reviewRate = reviewTotal
        ? Math.round((c.reviewed / reviewTotal) * 100)
        : 0;
      const atRisk = c.atRiskPeople.size;
      const quiet = c.quietPeople.size;
      const offTrack = c.offTrackPeople.size;
      const onTrack = Math.max(0, people - offTrack - atRisk);
      const overdueSamples = [...c.overdueSamples]
        .sort((a, b) => b.days - a.days)
        .slice(0, 3);
      const avgLateDays = c.lateReviewDays.length
        ? Math.round(
            (c.lateReviewDays.reduce((s, n) => s + n, 0) /
              c.lateReviewDays.length) *
              10
          ) / 10
        : 0;

      let grade = 100;
      grade -= c.overdue * 12;
      grade -= Math.min(c.pending, 8) * 4;
      grade -= Math.min(c.stalePending, 6) * 3;
      grade -= quiet * 6;
      grade -= offTrack * 5;
      grade -= atRisk * 4;
      if (avgLateDays > 0) grade -= Math.min(15, Math.round(avgLateDays * 2));
      grade = Math.max(0, Math.min(100, Math.round(grade)));

      const health =
        grade < 55 || c.overdue > 0 || quiet >= 2
          ? "needs_attention"
          : grade < 75 || c.pending > 2 || offTrack > 0
            ? "watch"
            : "healthy";

      const issues = [];
      if (c.overdue > 0) {
        const samples = overdueSamples
          .map((s) => `${s.person}'s ${s.form} (${s.days} d)`)
          .join(", ");
        issues.push(
          `${c.overdue} client review${c.overdue === 1 ? "" : "s"} overdue${
            samples ? ` — ${samples}${c.overdue > overdueSamples.length ? "…" : ""}` : ""
          }`
        );
      }
      if (c.stalePending > 0) {
        issues.push(
          `${c.stalePending} check-in${c.stalePending === 1 ? "" : "s"} from the last two weeks unread for 48 h+`
        );
      } else if (c.pending > 0) {
        issues.push(
          `${c.pending} review${c.pending === 1 ? "" : "s"} still pending`
        );
      }
      if (quiet > 0) {
        issues.push(
          `${quiet} client${quiet === 1 ? "" : "s"} with no contact for 14 days`
        );
      }
      if (offTrack > 0) {
        issues.push(
          `${offTrack} client${offTrack === 1 ? "" : "s"} off track on measurable progress`
        );
      }
      if (avgLateDays >= 1.5) {
        issues.push(`reviews done ${avgLateDays} days late on average`);
      }
      if (!issues.length && reviewTotal === 0) {
        issues.push("No review activity yet for assigned people");
      }

      return {
        id: c.id,
        name: c.name,
        people,
        submissions: c.submissions,
        pending: c.pending,
        overdue: c.overdue,
        reviewed: c.reviewed,
        reviewsDue: c.pending + c.overdue,
        trackers: c.trackers.size,
        reviewRate,
        health,
        atRisk,
        quiet,
        offTrack,
        onTrack,
        grade,
        stalePending: c.stalePending,
        avgLateDays,
        overdueSamples,
        issues,
        lastActivity: c.lastActivity,
        lastReviewAt: c.lastReviewAt,
      };
    })
    .sort(
      (a, b) =>
        a.grade - b.grade ||
        b.overdue - a.overdue ||
        b.reviewsDue - a.reviewsDue ||
        b.submissions - a.submissions ||
        a.name.localeCompare(b.name)
    );

  // —— Client activity ——
  const clientMap = new Map();
  allSubs.forEach((row) => {
    const key = getSubmissionPersonKey(row);
    if (!key) return;
    if (!clientMap.has(key)) {
      clientMap.set(key, {
        key,
        name: personDisplayName(row),
        email: row.email || "",
        source: row.source,
        submissions: 0,
        trackers: new Set(),
        submittedAts: [],
        lastAt: row.submittedAt,
        firstAt: row.submittedAt,
        statusName: row.statusName,
        statusColor: row.statusColor,
        coachName: row.coachName || "—",
        forms: new Set(),
        trackerId: row.trackerId,
        pendingReviews: 0,
        overdueReviews: 0,
        latestSubmissionId: row.id,
        latestSubmission: row,
      });
    }
    const c = clientMap.get(key);
    c.submissions += 1;
    if (row.submittedAt) c.submittedAts.push(row.submittedAt);
    if (row.trackerId) {
      c.trackers.add(row.trackerName || row.trackerId);
      c.trackerId = row.trackerId;
    }
    if (row.formId) c.forms.add(row.formId);
    if (row.reviewStatus === SUBMISSION_REVIEW.PENDING) c.pendingReviews += 1;
    if (row.reviewStatus === SUBMISSION_REVIEW.OVERDUE) c.overdueReviews += 1;
    if (moment(row.submittedAt).isBefore(moment(c.firstAt))) {
      c.firstAt = row.submittedAt;
    }
    if (moment(row.submittedAt).isAfter(moment(c.lastAt))) {
      c.lastAt = row.submittedAt;
      c.statusName = row.statusName;
      c.statusColor = row.statusColor;
      c.coachName = row.coachName || c.coachName;
      c.latestSubmissionId = row.id;
      c.latestSubmission = row;
    }
  });

  const clients = Array.from(clientMap.values())
    .map((c) => {
      const tracker = trackerById.get(c.trackerId);
      const target = checkpointTarget(tracker);
      const formsCount = c.forms.size;
      const progress = Math.min(100, Math.round((formsCount / target) * 100));
      const daysSince = moment().diff(moment(c.lastAt), "days");
      const finished = isFinishStatus(c.statusName, tracker);
      const dropped = isDropStatus(c.statusName, tracker);
      let activity = activityLabel(daysSince);
      if (finished) activity = "completed";
      if (dropped) activity = "dropped";
      const performance = summarizeClientPerformance(c.latestSubmission);
      const sortedAts = [...(c.submittedAts || [])].sort(
        (a, b) => moment(a).valueOf() - moment(b).valueOf()
      );
      const activeCutoff = moment().subtract(7, "days");
      const activeSubmissions = sortedAts.filter((at) =>
        moment(at).isSameOrAfter(activeCutoff)
      ).length;
      let onTime = 0;
      sortedAts.forEach((at, i) => {
        if (i === 0) {
          onTime += 1;
          return;
        }
        if (moment(at).diff(moment(sortedAts[i - 1]), "days") <= 7) {
          onTime += 1;
        }
      });
      return {
        key: c.key,
        name: c.name,
        email: c.email,
        source: c.source,
        submissions: c.submissions,
        trackers: Array.from(c.trackers),
        trackerCount: c.trackers.size,
        activeSubmissions,
        onTime,
        formsCount,
        progress,
        lastAt: c.lastAt,
        firstAt: c.firstAt,
        daysSince,
        statusName: c.statusName,
        statusColor: c.statusColor,
        coachName: c.coachName,
        activity,
        pendingReviews: c.pendingReviews,
        overdueReviews: c.overdueReviews,
        finished,
        dropped,
        latestSubmissionId: c.latestSubmissionId,
        performance,
      };
    })
    .sort(
      (a, b) =>
        (a.activity === "at_risk" ? 0 : 1) - (b.activity === "at_risk" ? 0 : 1) ||
        b.overdueReviews - a.overdueReviews ||
        b.submissions - a.submissions ||
        moment(b.lastAt).valueOf() - moment(a.lastAt).valueOf()
    );

  const improvingPeople = clients.filter((c) => c.performance?.verdict === "up")
    .length;
  const decliningPeople = clients.filter(
    (c) => c.performance?.verdict === "down"
  ).length;
  const mixedPeople = clients.filter((c) => c.performance?.verdict === "mixed")
    .length;

  const statusJourney = {};
  peopleLatest.forEach((row) => {
    const label = row.statusName || "Unknown";
    statusJourney[label] = (statusJourney[label] || 0) + 1;
  });

  const completionRate = uniquePeople
    ? Math.round((completedPeople / uniquePeople) * 100)
    : 0;

  const multiSubmitPeople = clients.filter((c) => c.submissions > 1).length;
  const atRiskClients = clients.filter((c) => c.activity === "at_risk").length;
  const activeClients = clients.filter((c) => c.activity === "active").length;
  const coolingClients = clients.filter((c) => c.activity === "cooling").length;

  const reviewActionable =
    reviewBreakdown.pending + reviewBreakdown.overdue + reviewBreakdown.reviewed;
  const reviewHealthPct = reviewActionable
    ? Math.round((reviewBreakdown.reviewed / reviewActionable) * 100)
    : 100;

  // —— Attention items (actionable for admin) ——
  const attention = [];
  if (reviewsOverdue > 0) {
    attention.push({
      id: "overdue_reviews",
      severity: "high",
      title: `${reviewsOverdue} overdue coach review${reviewsOverdue === 1 ? "" : "s"}`,
      detail: "People are waiting — coaches need to catch up.",
      href: "/tracker-submissions",
    });
  }
  if (atRiskClients > 0) {
    attention.push({
      id: "at_risk_clients",
      severity: "high",
      title: `${atRiskClients} ${atRiskClients === 1 ? "person" : "people"} going quiet`,
      detail: "No submission in 21+ days — follow up before they drop.",
      href: "/tracker-submissions",
    });
  }
  if (decliningPeople > 0) {
    attention.push({
      id: "declining_performance",
      severity: "high",
      title: `${decliningPeople} ${decliningPeople === 1 ? "person" : "people"} declining`,
      detail: "Measurable metrics moving the wrong way vs baseline.",
      href: "/tracker-submissions",
    });
  }
  if (reviewsPending > 3) {
    attention.push({
      id: "pending_reviews",
      severity: "medium",
      title: `${reviewsPending} reviews still pending`,
      detail: "Queued for coaches — keep the queue moving.",
      href: "/tracker-submissions",
    });
  }
  const weakTrackers = trackerPerf.filter(
    (t) => t.live && t.people >= 2 && t.completionRate < 25 && t.atRisk > 0
  );
  if (weakTrackers.length > 0) {
    attention.push({
      id: "weak_trackers",
      severity: "medium",
      title: `${weakTrackers.length} tracker${weakTrackers.length === 1 ? "" : "s"} underperforming`,
      detail: weakTrackers
        .slice(0, 2)
        .map((t) => t.name)
        .join(", "),
      href: "/trackers",
    });
  }
  const overloadedCoaches = coaches.filter(
    (c) => c.id !== "unassigned" && (c.overdue > 0 || c.reviewsDue >= 4)
  );
  if (overloadedCoaches.length > 0) {
    attention.push({
      id: "coach_load",
      severity: "medium",
      title: `${overloadedCoaches.length} coach${overloadedCoaches.length === 1 ? "" : "es"} need attention`,
      detail: overloadedCoaches
        .slice(0, 2)
        .map((c) => c.name)
        .join(", "),
      href: "/tracker-submissions",
    });
  }
  if (droppedPeople > 0) {
    attention.push({
      id: "dropped",
      severity: "low",
      title: `${droppedPeople} drop-out${droppedPeople === 1 ? "" : "s"}`,
      detail: "Check why they left and recover where you can.",
      href: "/tracker-submissions",
    });
  }

  const activityMix = {
    active: activeClients,
    cooling: coolingClients,
    at_risk: atRiskClients,
    completed: clients.filter((c) => c.activity === "completed").length,
    dropped: clients.filter((c) => c.activity === "dropped").length,
  };

  const trackersWithPendingReviews = new Set();
  allSubs.forEach((r) => {
    if (
      (r.reviewStatus === SUBMISSION_REVIEW.PENDING ||
        r.reviewStatus === SUBMISSION_REVIEW.OVERDUE) &&
      r.trackerId
    ) {
      trackersWithPendingReviews.add(r.trackerId);
    }
  });

  const reviewsCompleted = reviewBreakdown.reviewed;

  const recentSubmissions = [...allSubs]
    .sort(
      (a, b) =>
        moment(b.submittedAt).valueOf() - moment(a.submittedAt).valueOf()
    )
    .slice(0, 12);

  return {
    kpis: {
      trackers: allTrackers.length,
      live: liveTrackers.length,
      draftOrOther: allTrackers.length - liveTrackers.length,
      people: uniquePeople,
      submissions: allSubs.length,
      reviewsDue,
      reviewsPending,
      reviewsOverdue,
      reviewsCompleted,
      trackersWithPendingReviews: trackersWithPendingReviews.size,
      completedPeople,
      droppedPeople,
      inProgressPeople,
      completionRate,
      reviewHealthPct,
      coachesActive: coaches.filter(
        (c) => c.id !== "unassigned" && c.submissions > 0
      ).length,
      coachesAssigned: coaches.filter((c) => c.id !== "unassigned").length,
      multiSubmitPeople,
      atRiskClients,
      activeClients,
      improvingPeople,
      decliningPeople,
      mixedPeople,
      thisWeekSubs,
      lastWeekSubs,
      peopleActiveThisWeek: peopleActiveThisWeek.size,
      peopleActiveLastWeek: peopleActiveLastWeek.size,
      avgSubmissionsPerPerson: uniquePeople
        ? Math.round((allSubs.length / uniquePeople) * 10) / 10
        : 0,
    },
    reviewBreakdown,
    sourceBreakdown,
    activityMix,
    trend: {
      labels: dayLabels,
      counts: dayCounts,
      newPeople: dayNewPeople,
    },
    trackerPerf,
    coaches,
    clients,
    attention,
    recentSubmissions,
    statusJourney: Object.entries(statusJourney)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count),
  };
};
